import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router';
import { styled } from '@nykaa/ui-components';
import { slideInRightAnimation } from 'shared/styles/animations/slideInRight';
import { fadeInAnimation } from 'shared/styles/animations/fadeIn';
import { getDeviceType, DEVICE_TYPE } from 'shared/helpers/app';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

export type AnimationType = 'slide' | 'fade' | 'none';

export interface PageTransitionProps {
  children: React.ReactNode;
  patterns?: RegExp[];
  fromPatterns?: RegExp[];
}

const SlideFromRight = styled.div<{ duration: number }>`
  animation: ${slideInRightAnimation} ${({ duration }) => duration}ms ease-out;
`;

const FadeIn = styled.div<{ duration: number }>`
  animation: ${fadeInAnimation} ${({ duration }) => duration}ms ease-out;
`;

function matchesPatterns(pathname: string, patterns?: RegExp[]): boolean {
  if (!patterns || patterns.length === 0) return true;
  return patterns.some((pattern) => pattern.test(pathname));
}

function PageTransition({ children, patterns, fromPatterns }: PageTransitionProps) {
  const location = useLocation();
  const history = useHistory();
  const prevPathname = useRef(location.pathname);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [animState, setAnimState] = useState<{
    key: number;
    shouldAnimate: boolean;
  }>({
    key: 0,
    shouldAnimate: false,
  });
  const { enabled, duration, forwardAnimation } =
    useRemoteConfig('pageNavigationAnimation') ?? {
      enabled: false,
      duration: 0,
      forwardAnimation: 'none' as AnimationType,
    };

  useEffect(() => {
    if (!enabled || getDeviceType() !== DEVICE_TYPE.MOBILE) {
      return;
    }

    const unlisten = history.listen((loc, action) => {
      if (loc.pathname !== prevPathname.current) {
        const from = prevPathname.current;
        prevPathname.current = loc.pathname;

        if (action === 'POP') {
          return;
        }

        const shouldAnimate = matchesPatterns(from, fromPatterns);
        setAnimState((prev) => ({ key: prev.key + 1, shouldAnimate }));
      }
    });

    return unlisten;
  }, [enabled, history, fromPatterns]);

  // Re-trigger CSS animation without remounting children:
  // reset animation to none, then restore on next frame so the browser replays it.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || animState.key === 0) return;
    el.style.animation = 'none';
    requestAnimationFrame(() => {
      el.style.animation = '';
    });
  }, [animState.key]);

  if (!enabled || getDeviceType() !== DEVICE_TYPE.MOBILE) {
    return <>{children}</>;
  }

  if (!animState.shouldAnimate || !matchesPatterns(location.pathname, patterns)) {
    return <>{children}</>;
  }

  if (forwardAnimation === 'none') {
    return <>{children}</>;
  }

  if (forwardAnimation === 'fade') {
    return (
      <FadeIn ref={wrapperRef} duration={duration}>
        {children}
      </FadeIn>
    );
  }

  return (
    <SlideFromRight ref={wrapperRef} duration={duration}>
      {children}
    </SlideFromRight>
  );
}

export default PageTransition;
